/**
 * Terms Acceptance Modal
 * Displays EULA/terms content that users must accept before accessing the application.
 * This modal cannot be dismissed without accepting the terms.
 * Fully responsive for web, tablet, and mobile devices.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, ActionButton, LoadingSpinner } from '@turn-connect/ui';
import { theme } from '@turn-connect/ui';
import { TermsDocument } from '../../services/terms';
import { useTermsAcceptance } from '../../hooks/useTermsAcceptance';

interface TermsAcceptanceModalProps {
  isOpen: boolean;
  pendingTerms: TermsDocument[];
  onAcceptComplete: () => void;
  onError?: (error: string) => void;
}

// Hook to detect screen size for responsive styling
const useResponsiveBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setBreakpoint('mobile');
      } else if (width < 768) {
        setBreakpoint('tablet');
      } else {
        setBreakpoint('desktop');
      }
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  return breakpoint;
};

export const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({ isOpen, pendingTerms, onAcceptComplete, onError }) => {
  const [currentTermsIndex, setCurrentTermsIndex] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const breakpoint = useResponsiveBreakpoint();

  const { termsContent, isLoadingContent, isAcceptingTerms, termsError, loadTermsContent, acceptTerms, clearError } = useTermsAcceptance();

  const currentTerms = pendingTerms[currentTermsIndex];

  // Responsive style values
  const isMobile = breakpoint === 'mobile';
  const isTablet = breakpoint === 'tablet';
  const spacing = {
    container: isMobile ? theme.spacing[2] : theme.spacing[3],
    content: isMobile ? theme.spacing[3] : theme.spacing[4],
  };
  const fontSize = {
    small: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm,
    normal: isMobile ? theme.typography.fontSize.sm : theme.typography.fontSize.md,
    large: isMobile ? theme.typography.fontSize.md : theme.typography.fontSize.lg,
  };
  const checkboxSize = isMobile ? 22 : 20;

  // Load terms content when modal opens or when moving to next terms
  useEffect(() => {
    if (isOpen && currentTerms) {
      setHasScrolledToBottom(false);
      setHasAgreed(false);
      loadTermsContent(currentTerms.type);
    }
  }, [isOpen, currentTerms, loadTermsContent]);

  // Handle scroll tracking
  const handleScroll = useCallback(() => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Consider scrolled to bottom when within 50px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    }
  }, []);

  // Handle accept button click
  const handleAccept = async () => {
    if (!currentTerms) return;

    clearError();
    const success = await acceptTerms(currentTerms.id);

    if (success) {
      // Check if there are more terms to accept
      if (currentTermsIndex < pendingTerms.length - 1) {
        setCurrentTermsIndex((prev) => prev + 1);
      } else {
        // All terms accepted
        onAcceptComplete();
      }
    } else if (onError && termsError) {
      onError(termsError);
    }
  };

  // Handle retry after error
  const handleRetry = () => {
    clearError();
    if (currentTerms) {
      loadTermsContent(currentTerms.type);
    }
  };

  const canAccept = hasScrolledToBottom && hasAgreed && !isAcceptingTerms;

  // Don't render if no pending terms
  if (!currentTerms) return null;

  // Custom empty onClose - terms cannot be dismissed
  const handleClose = () => {
    // Do nothing - modal cannot be closed without accepting
  };

  return (
    <Modal
      isOpen={isOpen}
      title={currentTerms.title || 'Terms and Conditions'}
      onClose={handleClose}
      showCloseButton={false} // No close button - must accept
      closeOnBackdropClick={false} // Cannot click outside to close
      mobileFullScreen={true}
      stickyFooter={true}
      width={isMobile ? '100%' : isTablet ? 'min(700px, 95vw)' : 'min(800px, 95vw)'}
      maxHeight={isMobile ? '100dvh' : '90vh'}
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.container,
            width: '100%',
            padding: isMobile ? `${theme.spacing[2]}px 0` : 0,
          }}
        >
          {/* Agreement checkbox - larger touch target for mobile */}
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: isMobile ? theme.spacing[3] : theme.spacing[2],
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
              opacity: hasScrolledToBottom ? 1 : 0.5,
              color: theme.colors.text.primary,
              fontSize: fontSize.normal,
              padding: isMobile ? `${theme.spacing[2]}px 0` : 0,
              // Larger touch target for mobile
              minHeight: isMobile ? 44 : 'auto',
            }}
          >
            <input
              type="checkbox"
              checked={hasAgreed}
              onChange={(e) => setHasAgreed(e.target.checked)}
              disabled={!hasScrolledToBottom}
              style={{
                marginTop: isMobile ? 0 : 2,
                accentColor: theme.colors.brand.primary,
                width: checkboxSize,
                height: checkboxSize,
                minWidth: checkboxSize,
                minHeight: checkboxSize,
                cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
                flexShrink: 0,
              }}
            />
            <span style={{ lineHeight: 1.4 }}>
              I have read and agree to the {currentTerms.title || 'terms and conditions'}
              {currentTerms.version && !isMobile && ` (Version ${currentTerms.version})`}
            </span>
          </label>

          {/* Buttons - stack vertically on mobile */}
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column-reverse' : 'row',
              justifyContent: 'flex-end',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: spacing.container,
            }}
          >
            {pendingTerms.length > 1 && (
              <span
                style={{
                  color: theme.colors.text.secondary,
                  fontSize: fontSize.small,
                  alignSelf: 'center',
                  marginRight: isMobile ? 0 : 'auto',
                  textAlign: isMobile ? 'center' : 'left',
                }}
              >
                {currentTermsIndex + 1} of {pendingTerms.length}
              </span>
            )}
            <div style={{ width: isMobile ? '100%' : 'auto' }}>
              <ActionButton variant="primary" onClick={handleAccept} disabled={!canAccept} loading={isAcceptingTerms} fullWidth={isMobile}>
                {isAcceptingTerms ? 'Accepting...' : 'Accept'}
              </ActionButton>
            </div>
          </div>
        </div>
      }
    >
      <div
        data-testid="terms-modal"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          // minHeight: 0 is critical for flex children to shrink below content size.
          // On iOS Safari, without this, the flex child expands to fit all content,
          // which prevents overflow scrolling from activating on nested elements.
          minHeight: 0,
        }}
      >
        {/* Version info */}
        {currentTerms.version && (
          <div
            style={{
              color: theme.colors.text.secondary,
              fontSize: fontSize.small,
              marginBottom: theme.spacing[2],
            }}
          >
            Version {currentTerms.version} - Effective: {new Date(currentTerms.effective_date).toLocaleDateString()}
          </div>
        )}

        {/* Scroll instruction */}
        {!hasScrolledToBottom && !isLoadingContent && !termsError && (
          <div
            style={{
              backgroundColor: theme.colors.background.quaternary,
              color: theme.colors.text.secondary,
              padding: `${theme.spacing[2]}px ${spacing.content}px`,
              borderRadius: theme.borders.radius.md,
              marginBottom: spacing.container,
              fontSize: fontSize.small,
              textAlign: 'center',
            }}
          >
            {isMobile ? 'Scroll to read the entire document' : 'Please scroll through and read the entire document to enable acceptance'}
          </div>
        )}

        {/* Content area - responsive height */}
        <div
          ref={contentRef}
          onScroll={handleScroll}
          data-testid="terms-content"
          className="terms-content"
          style={{
            flex: '1 1 0%',
            // minHeight: 0 allows this flex child to shrink below its content height,
            // which is required for overflowY: auto to activate scrolling.
            // Without this, iOS Safari expands the element to fit all content and
            // the footer gets pushed offscreen.
            minHeight: 0,
            overflowY: 'auto',
            backgroundColor: theme.colors.background.secondary,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borders.radius.md,
            padding: spacing.content,
            maxHeight: isTablet ? '55vh' : isMobile ? undefined : '50vh',
            // Enable smooth scrolling on mobile
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain', // Prevent scroll chaining on iOS
          }}
        >
          {/* Loading state */}
          {isLoadingContent && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: isMobile ? 150 : 200,
                gap: theme.spacing[3],
              }}
            >
              <LoadingSpinner size={isMobile ? 'medium' : 'large'} />
              <span style={{ color: theme.colors.text.secondary, fontSize: fontSize.normal }}>Loading terms...</span>
            </div>
          )}

          {/* Error state */}
          {termsError && !isLoadingContent && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: isMobile ? 150 : 200,
                gap: theme.spacing[3],
                textAlign: 'center',
                padding: spacing.content,
              }}
            >
              <div
                style={{
                  color: theme.colors.status.error,
                  fontSize: fontSize.large,
                }}
              >
                Failed to load terms content
              </div>
              <div style={{ color: theme.colors.text.secondary, fontSize: fontSize.normal }}>{termsError}</div>
              <ActionButton variant="secondary" onClick={handleRetry}>
                Retry
              </ActionButton>
            </div>
          )}

          {/* Terms content */}
          {!isLoadingContent && !termsError && termsContent && (
            <div
              dangerouslySetInnerHTML={{ __html: termsContent }}
              style={{
                color: theme.colors.text.primary,
                lineHeight: 1.6,
                fontSize: fontSize.normal,
              }}
            />
          )}
        </div>

        {/* Scroll indicator */}
        {hasScrolledToBottom && !termsError && (
          <div
            style={{
              color: theme.colors.status.success,
              fontSize: fontSize.small,
              marginTop: theme.spacing[2],
              textAlign: 'center',
              padding: isMobile ? `${theme.spacing[1]}px 0` : 0,
            }}
          >
            {isMobile ? '✓ Document reviewed' : 'You have reviewed the complete document'}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TermsAcceptanceModal;
